export type HubScopedSearchScope = "all" | "sku";

const SCOPE_TOKEN_PATTERN = "sku|skus";

const AT_SCOPE_RE = new RegExp(`^@(${SCOPE_TOKEN_PATTERN})(?:\\s+|$)`, "i");
const COLON_SCOPE_RE = new RegExp(`^(${SCOPE_TOKEN_PATTERN}):\\s*(.*)$`, "i");

/** Minimum characters before unscoped document list search hits the API. */
export const DOCUMENT_LIST_SEARCH_MIN_ALL_TERM_LENGTH = 2;

/** Minimum characters for @sku / sku: scoped list search. */
export const DOCUMENT_LIST_SEARCH_MIN_SKU_TERM_LENGTH = 1;

/** Cap rows returned when a search term is applied (UI and MCP). */
export const DOCUMENT_LIST_SEARCH_RESULT_LIMIT = 300;

export function parseHubScopedSearchQuery(raw: string): { scope: HubScopedSearchScope; term: string } {
  const trimmed = raw.trimStart();
  const atMatch = AT_SCOPE_RE.exec(trimmed);
  if (atMatch) {
    return { scope: "sku", term: trimmed.slice(atMatch[0].length).trim() };
  }
  const colonMatch = COLON_SCOPE_RE.exec(trimmed);
  if (colonMatch) {
    return { scope: "sku", term: (colonMatch[2] ?? "").trim() };
  }
  return { scope: "all", term: raw.trim() };
}

export function resolveDocumentListSearch(
  qRaw: string | null | undefined,
  searchScope: string | null | undefined,
): { scope: HubScopedSearchScope; term: string; skuOnlySearch: boolean } | null {
  const trimmed = qRaw?.trim();
  if (!trimmed) return null;
  const parsed = parseHubScopedSearchQuery(trimmed);
  const skuOnlySearch = searchScope?.trim() === "sku" || parsed.scope === "sku";
  const term = parsed.term;
  if (!term) return null;
  const minLength = skuOnlySearch
    ? DOCUMENT_LIST_SEARCH_MIN_SKU_TERM_LENGTH
    : DOCUMENT_LIST_SEARCH_MIN_ALL_TERM_LENGTH;
  if (term.length < minLength) return null;
  return { scope: parsed.scope, term, skuOnlySearch };
}

/**
 * Appends document list search predicates to `clauses` and `params`.
 * Returns whether a search filter was applied.
 */
export function appendDocumentListSearchClause(
  params: unknown[],
  clauses: string[],
  qRaw: string | null | undefined,
  searchScope: string | null | undefined,
): boolean {
  const resolved = resolveDocumentListSearch(qRaw, searchScope);
  if (!resolved) return false;
  params.push(resolved.term);
  const paramIndex = params.length;
  if (resolved.skuOnlySearch) {
    clauses.push(`exists (
      select 1
        from documents.document_record_target
       where documents.document_record_target.document_record_id = documents.document_record.id
         and documents.document_record_target.target_type = 'sku'
         and coalesce(documents.document_record_target.sku, '') ilike '%' || $${paramIndex} || '%'
    )`);
    return true;
  }
  clauses.push(`(
    documents.document_record.title ilike '%' || $${paramIndex} || '%'
    or documents.document_record.original_filename ilike '%' || $${paramIndex} || '%'
    or coalesce(documents.document_record.description, '') ilike '%' || $${paramIndex} || '%'
    or coalesce(documents.document_record.change_notes, '') ilike '%' || $${paramIndex} || '%'
    or coalesce(documents.document_record.engineering_change_notice, '') ilike '%' || $${paramIndex} || '%'
    or exists (
      select 1
        from public.user_setting
        left join auth.users on auth.users.id = public.user_setting.id
       where public.user_setting.id = documents.document_record.owner_user_id
         and (
           coalesce(public.user_setting.display_name, '') ilike '%' || $${paramIndex} || '%'
           or coalesce(auth.users.email, '') ilike '%' || $${paramIndex} || '%'
         )
    )
    or exists (
      select 1
        from documents.document_record_target
        left join documents.product_collection on documents.product_collection.id = documents.document_record_target.product_collection_id
       where documents.document_record_target.document_record_id = documents.document_record.id
         and (
           coalesce(documents.document_record_target.sku, '') ilike '%' || $${paramIndex} || '%'
           or coalesce(documents.product_collection.code, '') ilike '%' || $${paramIndex} || '%'
           or coalesce(documents.product_collection.name, '') ilike '%' || $${paramIndex} || '%'
         )
    )
  )`);
  return true;
}
